import React from "react";
import { Search, ArrowRight } from "lucide-react";
import { useSuggestProductDrawer } from "@/contexts/SuggestProductDrawerContext";

/**
 * "Didn't find what you were looking for?" style CTA row. Rewritten copy:
 * ─ heading  : "Can't find what you need?"
 * ─ subhead  : "Tell us what you're looking for — our sourcing team gets back within 24 hours."
 * ─ CTA      : "Suggest a Product"  →  opens the SuggestProductDrawer.
 *
 * Optional props:
 *   query    — the current search term, so the drawer can pre-fill it.
 *   category — the current category slug, likewise.
 *   variant  — "card" (default, framed panel) or "minimal" (subtle inline row).
 */
export default function SuggestProductStrip({ query, category, variant = "card" }) {
  const { openSuggestProduct } = useSuggestProductDrawer();

  const trigger = () => openSuggestProduct({
    query: query || "",
    category: category || "",
  });

  if (variant === "minimal") {
    return (
      <div
        data-testid="suggest-strip-minimal"
        className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl bg-[#F3EFFC] border border-[#4B2AAD]/15 px-4 py-3"
      >
        <p className="text-sm text-gray-700">
          <span className="font-bold text-[#4B2AAD]">Can't find what you need?</span>{" "}
          Tell us and our sourcing team will get back within 24 hours.
        </p>
        <button
          type="button"
          data-testid="suggest-strip-cta"
          onClick={trigger}
          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-lg bg-[#4B2AAD] hover:bg-[#361D82] text-white text-sm font-semibold px-4 py-2 transition-colors"
        >
          Suggest a Product <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <section
      data-testid="suggest-strip"
      className="mt-10 rounded-2xl border border-[#4B2AAD]/15 bg-gradient-to-r from-[#F3EFFC] via-white to-[#F3EFFC] p-6 sm:p-7"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-11 w-11 rounded-xl bg-[#4B2AAD]/10 flex items-center justify-center">
            <Search className="h-5 w-5 text-[#4B2AAD]" />
          </div>
          <div>
            <h3 className="font-display text-lg sm:text-xl font-bold text-gray-900">
              Can't find what you need?
            </h3>
            <p className="text-sm text-gray-600 mt-0.5 max-w-xl">
              Tell us the product you're looking for — our sourcing team will find it and get back to you within{" "}
              <span className="font-semibold text-gray-800">24 hours</span>.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="suggest-strip-cta"
          onClick={trigger}
          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] text-white text-sm font-bold px-5 py-2.5 transition-colors active:scale-[0.98]"
        >
          Suggest a Product <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}
